'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../button';
import { Input } from '../input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../card';
import { Alert } from '../alert';
export function LoginForm({ onLogin, loading = false, error, title = 'Sign In', description = 'Enter your credentials to access your account', developmentDefaults }) {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    // Update form data when developmentDefaults change or component mounts
    useEffect(() => {
        if (developmentDefaults) {
            setFormData({
                email: developmentDefaults.email || '',
                password: developmentDefaults.password || '',
            });
        }
    }, [developmentDefaults]);
    const [showPassword, setShowPassword] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const validateForm = () => {
        const errors = {};
        if (!formData.email) {
            errors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (!formData.password) {
            errors.password = 'Password is required';
        }
        else if (formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            await onLogin(formData);
        }
        catch (err) {
            // Error handling is done by parent component
        }
    };
    const handleInputChange = (field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value,
        }));
        // Clear field error when user starts typing
        if (formErrors[field]) {
            setFormErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };
    return (_jsxs(Card, { className: "w-full max-w-md mx-auto", children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx(CardTitle, { className: "text-2xl", children: title }), _jsx(CardDescription, { children: description }), developmentDefaults && (_jsxs("div", { className: "mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700", children: [_jsx("div", { className: "font-medium mb-1", children: "\uD83E\uDDEA Development Mode" }), _jsxs("div", { children: ["Email: ", developmentDefaults.email] }), _jsxs("div", { children: ["Password: ", developmentDefaults.password] })] }))] }), _jsxs(CardContent, { children: [_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [error && (_jsx(Alert, { variant: "destructive", children: error })), _jsx("div", { children: _jsx(Input, { label: "Email", type: "email", value: formData.email, onChange: handleInputChange('email'), error: formErrors.email, placeholder: "Enter your email", required: true, disabled: loading }) }), _jsx("div", { children: _jsx(Input, { label: "Password", type: showPassword ? 'text' : 'password', value: formData.password, onChange: handleInputChange('password'), error: formErrors.password, placeholder: "Enter your password", required: true, disabled: loading, rightElement: _jsx("button", { type: "button", onClick: () => setShowPassword(prev => !prev), className: "text-gray-400 hover:text-gray-600 focus:outline-none", tabIndex: -1, disabled: loading, children: showPassword ? _jsx(Eye, { size: 16 }) : _jsx(EyeOff, { size: 16 }) }) }) }), _jsx(Button, { type: "submit", className: "w-full", loading: loading, disabled: loading, children: loading ? 'Signing In...' : 'Sign In' }), developmentDefaults && (_jsx(Button, { type: "button", variant: "outline", className: "w-full", disabled: loading, onClick: async () => {
                                    setFormData({
                                        email: developmentDefaults.email,
                                        password: developmentDefaults.password,
                                    });
                                    setFormErrors({});
                                    try {
                                        await onLogin(developmentDefaults);
                                    }
                                    catch (err) {
                                        // Error handling is done by parent component
                                    }
                                }, children: "\uD83D\uDE80 Quick Login (Dev)" }))] }), _jsx("div", { className: "mt-4 text-center", children: _jsx("button", { type: "button", className: "text-sm text-blue-600 hover:underline", disabled: loading, children: "Forgot your password?" }) })] })] }));
}
